import { Link } from "react-router-dom";
import { LOGO_URL } from "@/lib/gateways";
import useSEO from "@/hooks/useSEO";
import MinisterCredential from "@/components/MinisterCredential";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2
      className="font-sans-body text-[10px] tracking-[0.36em] uppercase mb-3"
      style={{ color: "var(--olive)" }}
    >
      {title}
    </h2>
    <div
      className="font-sans-body text-[14px] leading-relaxed space-y-4"
      style={{ color: "var(--ink)" }}
    >
      {children}
    </div>
  </section>
);

const PrivacyPolicy = () => {
  const effective = "December 2025";

  useSEO({
    title: "Privacy Policy — Selah & Stone Legacy Holdings",
    description:
      "How Selah and Stone Legacy Holdings collects, uses, and protects your information. Read our full privacy policy.",
    path: "/privacy",
  });

  return (
    <div className="paper-grain relative min-h-screen" data-testid="privacy-page">
      <div className="relative z-10">
        {/* Top bar */}
        <div className="px-6 sm:px-10 pt-8">
          <div className="mx-auto max-w-3xl flex items-start justify-between">
            <Link to="/" className="leading-tight">
              <div
                className="font-sans-body text-[10px] sm:text-[11px] tracking-[0.32em] uppercase"
                style={{ color: "var(--ink)" }}
              >
                Selah &nbsp;&middot;&nbsp; Stone
              </div>
              <div
                className="font-sans-body text-[9px] sm:text-[10px] tracking-[0.42em] uppercase mt-1"
                style={{ color: "var(--olive)" }}
              >
                Legacy Holdings
              </div>
            </Link>
            <Link
              to="/"
              className="font-sans-body text-[11px] tracking-[0.22em] uppercase pt-1 underline hover:no-underline"
              style={{ color: "var(--stone-deep)" }}
              data-testid="privacy-back-link"
            >
              Back
            </Link>
          </div>
        </div>

        {/* Header */}
        <header className="px-6 sm:px-10 pt-16 pb-10">
          <div className="mx-auto max-w-3xl">
            <img
              src={LOGO_URL}
              alt="Selah and Stone Legacy Holdings"
              className="h-16 w-16 mb-6"
            />
            <p
              className="font-sans-body text-[10px] tracking-[0.42em] uppercase"
              style={{ color: "var(--olive)" }}
            >
              Privacy Policy
            </p>
            <h1
              className="mt-3 font-serif-display text-5xl sm:text-6xl font-light leading-[1]"
              style={{ color: "var(--ink)" }}
            >
              Handled with
              <span
                className="block italic"
                style={{ color: "var(--olive)" }}
              >
                quiet care.
              </span>
            </h1>
            <p
              className="mt-6 font-serif-display italic text-xl"
              style={{ color: "var(--stone-deep)" }}
            >
              Effective {effective}
            </p>
          </div>
        </header>

        {/* Body */}
        <article className="px-6 sm:px-10 pb-20">
          <div className="mx-auto max-w-3xl">
            <p
              className="font-serif-display text-xl leading-relaxed italic"
              style={{ color: "var(--stone-deep)" }}
            >
              Selah and Stone Legacy Holdings, LLC (&ldquo;we,&rdquo;
              &ldquo;our,&rdquo; or &ldquo;us&rdquo;) respects your privacy.
              This Privacy Policy explains what we collect when you visit
              selahandstonelegacy.com or interact with our services, how we
              use it, and the choices you have.
            </p>

            <Section title="1. What we collect">
              <p>
                We collect only what you give us voluntarily, plus a minimum
                of technical data needed to run the site.
              </p>
              <p>
                <strong className="font-semibold">Waitlist / contact forms:</strong>{" "}
                your name, email address, the gateway or brand you're
                interested in, and any optional note you include.
              </p>
              <p>
                <strong className="font-semibold">Technical / analytics data:</strong>{" "}
                basic, aggregate usage information (e.g., pages viewed,
                approximate region, browser type) collected through PostHog
                product analytics to help us improve the experience. This
                data is not sold and is not used to identify you
                individually.
              </p>
              <p>
                We do not knowingly collect information from children under
                the age of 13.
              </p>
            </Section>

            <Section title="2. How we use your information">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  To notify you when a waitlist, beta, book, or service is
                  ready.
                </li>
                <li>
                  To respond to inquiries you submit through our forms or
                  email.
                </li>
                <li>
                  To understand how visitors use the site so we can improve
                  it.
                </li>
                <li>
                  To comply with legal obligations and protect our rights.
                </li>
              </ul>
              <p>
                We do not sell, rent, or trade your personal information.
              </p>
            </Section>

            <Section title="3. Who can see it">
              <p>
                Waitlist entries are stored in our private database and are
                viewable only by authorized Selah and Stone Legacy Holdings
                administrators.
              </p>
              <p>
                We use the following trusted service providers, each of whom
                processes limited data on our behalf and is bound by their
                own privacy commitments:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong className="font-semibold">Resend</strong> — delivers
                  transactional notification emails.
                </li>
                <li>
                  <strong className="font-semibold">MongoDB / managed hosting</strong>{" "}
                  — stores the waitlist database.
                </li>
                <li>
                  <strong className="font-semibold">PostHog</strong> —
                  anonymous, aggregate product analytics.
                </li>
              </ul>
              <p>
                We may also disclose information if required by law, court
                order, or to protect the safety of our family of brands or
                the public.
              </p>
            </Section>

            <Section title="4. Your choices">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong className="font-semibold">Unsubscribe:</strong> you
                  may ask to be removed from any waitlist or mailing at any
                  time by emailing{" "}
                  <a
                    href="mailto:contact@selahandstonelegacy.com"
                    className="underline"
                    style={{ color: "var(--olive)" }}
                  >
                    contact@selahandstonelegacy.com
                  </a>
                  .
                </li>
                <li>
                  <strong className="font-semibold">Access / delete:</strong>{" "}
                  you may request a copy of the information we hold on you,
                  or ask us to delete it. We will respond within a
                  reasonable period.
                </li>
                <li>
                  <strong className="font-semibold">Do Not Track:</strong>{" "}
                  we honor browser-level Do Not Track signals where
                  reasonably possible.
                </li>
              </ul>
            </Section>

            <Section title="5. Security">
              <p>
                We use industry-standard safeguards — including TLS
                encryption in transit, restricted administrative access, and
                password-protected admin tooling — to protect your
                information. No method of transmission or storage is 100%
                secure, but we treat your data with forensic-level care.
              </p>
            </Section>

            <Section title="6. Data retention">
              <p>
                We keep waitlist entries for as long as you remain
                interested in the corresponding service, or until you ask
                us to delete them. Analytics data is retained in aggregate
                form for a limited period to measure trends.
              </p>
            </Section>

            <Section title="7. Service-specific notes">
              <p>
                <strong className="font-semibold">Selah Slate<sup className="tm">TM</sup></strong>{" "}
                is a financial wellness initiative. We are not a law firm or
                credit repair clinic. Any information you submit through
                Selah Slate will be handled with the care appropriate for
                financial matters and kept strictly confidential.
              </p>
              <p>
                <strong className="font-semibold">
                  Selah and Stone Research<sup className="tm">TM</sup>
                </strong>{" "}
                performs background screening and open-source forensic due
                diligence under applicable law, including the Fair Credit
                Reporting Act (FCRA) where relevant. Research engagements
                are governed by a separate service agreement.
              </p>
              <p>
                <strong className="font-semibold">
                  Omni Signing Services<sup className="tm">TM</sup>
                </strong>{" "}
                provides notary public and loan signing services. We do not
                provide legal, financial, tax, or mortgage advice. Any
                planned mortgage origination services will be offered only
                through a properly licensed entity.
              </p>
            </Section>

            <Section title="8. Changes to this policy">
              <p>
                We may update this Privacy Policy from time to time. We
                will post any changes here with an updated effective date.
                Material changes will be communicated to active waitlist
                members by email.
              </p>
            </Section>

            <Section title="9. Contact us">
              <p>
                Questions, concerns, or requests? Reach us any time at{" "}
                <a
                  href="mailto:contact@selahandstonelegacy.com"
                  className="underline"
                  style={{ color: "var(--olive)" }}
                  data-testid="privacy-contact-link"
                >
                  contact@selahandstonelegacy.com
                </a>
                .
              </p>
              <p className="pt-2 font-serif-display italic" style={{ color: "var(--stone-deep)" }}>
                Selah and Stone Legacy Holdings, LLC
                <br />
                Registered in Hillsborough County, Florida, USA
              </p>
            </Section>

            <div className="rule-ornament mt-16">
              <span
                className="font-serif-display italic text-sm"
                style={{ color: "var(--stone-deep)" }}
              >
                selah
              </span>
            </div>

            <MinisterCredential />
          </div>
        </article>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
